import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { AssetDetailsAPI } from "../viewmodel/AssetDetailsAPI";

export const RETIRE_TYPES = [
  { value: "Retired", label: "Retired (too old / not usable)" },
  { value: "Lost/Stolen", label: "Lost / Stolen" },
  { value: "Sold", label: "Sold" },
  { value: "E-waste", label: "E-waste (disposed)" },
];
const MAX_LEN = 255;

const paper = {
  backgroundColor: "#ffffff",
  boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.064)",
  padding: "20px",
  borderRadius: "5px",
  position: "relative",
  width: "420px",
  maxWidth: "90vw",
};

// Popup: retire / lost / dispose an asset
const RetireModal = ({ open, onClose, onSuccess, cAssetData }) => {
  const [type, setType] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const { handleToastOpen } = useToast();

  useEffect(() => {
    if (!open) {
      setType("");
      setReason("");
      setTouched(false);
      setLoading(false);
    }
  }, [open]);

  const isValid = !!type && reason.trim().length > 0;

  const onRetire = async () => {
    if (!isValid) {
      setTouched(true);
      return;
    }
    try {
      setLoading(true);
      await AssetDetailsAPI.retireAsset(cAssetData.asset_id, type, reason.trim());
      handleToastOpen("success", "Asset retired successfully");
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      handleToastOpen("error", error.response?.data?.message || "Failed to retire asset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={paper}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Retire Asset
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <b>Asset ID:</b> {cAssetData?.asset_id}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <b>Category:</b> {cAssetData?.category || "-"}
        </Typography>

        <TextField
          select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          fullWidth
          required
          size="small"
          disabled={loading}
          error={touched && !type}
          helperText={touched && !type ? "Please choose the type" : " "}
        >
          {RETIRE_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Reason"
          placeholder="e.g. Stolen from cab, FIR no. 2231"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          multiline
          rows={3}
          fullWidth
          required
          disabled={loading}
          inputProps={{ maxLength: MAX_LEN }}
          error={touched && reason.trim().length === 0}
          helperText={touched && reason.trim().length === 0 ? "Please enter a reason" : " "}
        />
        <Typography variant="caption" sx={{ display: "block", textAlign: "right", color: "#888" }}>
          {reason.length} / {MAX_LEN}
        </Typography>

        <Typography variant="caption" sx={{ display: "block", color: "#666", mt: 1 }}>
          The asset is not deleted. It is hidden from the main list (turn on "Retired only" to
          see it) and can be restored later.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
          <Button variant="contained" color="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onRetire}
            disabled={!isValid || loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Retire"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Popup: restore a retired asset (it becomes Available again)
export const RestoreModal = ({ open, onClose, onSuccess, cAssetData }) => {
  const [loading, setLoading] = useState(false);
  const { handleToastOpen } = useToast();

  const onRestore = async () => {
    try {
      setLoading(true);
      await AssetDetailsAPI.restoreAsset(cAssetData.asset_id);
      handleToastOpen("success", "Asset restored. It is Available again.");
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      handleToastOpen("error", error.response?.data?.message || "Failed to restore asset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={paper}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Restore Asset
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Restore <b>{cAssetData?.asset_id}</b>? It will be <b>Available</b> again and show in
          the main list.
        </Typography>
        <Typography variant="body2" sx={{ color: "#666" }}>
          Use this if it was retired by mistake, or a lost asset was found.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="contained" color="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onRestore} disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Restore"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RetireModal;